import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'
import { uniq } from 'ramda'
import { ActionType } from 'typesafe-actions'
import { TPartyPayload } from '../party/types'
import { TSentApplicationInvoiceStatus } from '../recon/types'
/**
 * A direct module import, not the `../property` barrel: the barrel pulls in
 * `api/property/epics`, which imports `api/portfolio`, so this slice would sit inside a
 * state -> barrel -> epics -> state require cycle. `api/recon/state` imports this module,
 * and that cycle is what previously forced it to reference `sendApplicationInvoice_success`
 * by its raw string type.
 */
import { events as propertyApiEvents } from '../property/state'
import { NAMESPACE } from './constants'
import {
  TAmendPropertyRequest,
  TAmendRequest,
  TApplication,
  TApplicationsResponse,
  TApprovalFields,
  TCreateApplicationRequest,
  TCreatePortfolioRequest,
  TInvoiceSchedule,
  TPortfolio,
  TPortfolioListEntry,
  TPortfolioState,
  TPortfolioStatusRow,
  TPortfolioSummary,
  TSegmentFields,
  TTerminateRequestBody,
  TTerminationReasonSummary,
  TUpdateApplicationRequest,
} from './types'

const initialState: TPortfolioState = {
  allIds: [], // Used for ordering
  byId: {},
  summaries: [],
  /**
   * @todo introduce when we use search API
   * EG: `bySearchQuery: { 'query': ['portfolioId1', 'portfolioId2', 'portfolioId3'] }
   */
  // bySearchQuery: {},
  terminationReasons: [],
  invoiceSchedules: {},
  bulkInvoicesCsvTemplate: '',
  status: [],
  applications: {},
}

const handlePortfolioResponse = (state: Draft<TPortfolioState>, payload: TPortfolio) => {
  state.byId[payload.id] = payload
  state.allIds = uniq(state.allIds.concat(payload.id))
}

const portfolioApiSlice = createSlice({
  name: `${NAMESPACE}Api`,
  initialState,
  reducers: {
    portfolios_request: (state, { payload }: PayloadAction<{ id: string }>) => {},
    portfolios_success: (state, { payload }: PayloadAction<TPortfolioListEntry[]>) => {
      /** @todo refactor when API is updated with flatten portfolios */
      const portfolios = payload.flatMap(entry => entry.portfolios)
      state.allIds = portfolios.map(p => p.id)
      portfolios.forEach(p => {
        state.byId[p.id] = p
      })
    },
    portfolioSummaries_request: () => {},
    portfolioSummaries_success: (state, { payload }: PayloadAction<TPortfolioSummary[]>) => {
      state.summaries = payload
    },
    portfolio_request: (state, { payload }: PayloadAction<string>) => {},
    portfolio_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    createPortfolio_request: (state, { payload }: PayloadAction<TCreatePortfolioRequest>) => {},
    createPortfolio_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    amendProperty_request: (state, { payload }: PayloadAction<TAmendRequest<TAmendPropertyRequest>>) => {},
    amendProperty_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { previousPropertyId?: string }>) => {
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { previousPropertyId?: string }) => ({ payload, meta, error: null }),
    },
    amendTerms_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    amendTerms_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { portfolioId: string }>) => {
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { portfolioId: string }) => ({ payload, meta, error: null }),
    },
    amendRentAndFees_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    amendRentAndFees_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    amendCommission_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    amendCommission_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    amendSettings_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    amendSettings_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { portfolioId?: string }>) => {
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { portfolioId?: string }) => ({ payload, meta, error: null }),
    },
    amendMetaData_request: (state, { payload }: PayloadAction<TAmendRequest<{ data: string }>>) => {},
    amendMetaData_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    invoiceSchedules_request: (state, { payload }: PayloadAction<string>) => {},
    invoiceSchedules_success: (
      state,
      { payload }: PayloadAction<{ params: { id: string }; response: { schedules: TInvoiceSchedule[] } }>,
    ) => {
      state.invoiceSchedules[payload.params.id] = payload.response.schedules
    },
    approvalRequest_request: (state, { payload }: PayloadAction<TAmendRequest<TApprovalFields>>) => {},
    approvalRequest_success: (state, { payload }: PayloadAction<unknown>) => {},
    approve_request: (state, { payload }: PayloadAction<TAmendRequest<TApprovalFields>>) => {},
    approve_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { portfolioId?: string }>) => {
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { portfolioId?: string }) => ({ payload, meta, error: null }),
    },
    /** @todo re-introduce when multi-user is added */
    // declineRequested: () => {},
    // declined => () => {},
    addInvoiceTemplate_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    addInvoiceTemplate_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { portfolioId: string }>) => {
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { portfolioId: string }) => ({ payload, meta, error: null }),
    },
    updateInvoiceTemplate_request: (
      state,
      { payload }: PayloadAction<{ params: { id: string; invoiceId: string }; body: unknown }>,
    ) => {},
    updateInvoiceTemplate_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { portfolioId: string }>) => {
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { portfolioId: string }) => ({ payload, meta, error: null }),
    },
    invoiceTemplateChanged: (state, { payload }: PayloadAction<unknown>) => {},
    invoiceTemplateRemoved: (state, { payload }: PayloadAction<unknown>) => {},
    /** @todo will probably need to stage removed templates so ID's can be posted on save */
    removeInvoiceTemplate_request: (state, { payload }: PayloadAction<{ id: string; invoiceId: string }>) => {},
    removeInvoiceTemplate_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { portfolioId?: string }>) => {
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { portfolioId?: string }) => ({ payload, meta, error: null }),
    },
    terminationReasons_request: () => {},
    terminationReasons_success: (state, { payload }: PayloadAction<TTerminationReasonSummary[]>) => {
      state.terminationReasons = payload
    },
    terminateApprovedLease_request: (state, { payload }: PayloadAction<TAmendRequest<TTerminateRequestBody>>) => {},
    terminateApprovedLease_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    deleteDraftLease_request: (state, { payload }: PayloadAction<string>) => {},
    deleteDraftLease_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    renewal_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    renewal_success: {
      reducer: (state, { payload }: PayloadAction<TPortfolio, string, { id: string }>) => {
        // The follow-up `state.byId[payload.id].renewal = payload.renewal` this used to do
        // was a no-op: handlePortfolioResponse has already stored `payload` wholesale.
        handlePortfolioResponse(state, payload)
      },
      prepare: (payload: TPortfolio, meta: { id: string }) => ({ payload, meta, error: null }),
    },
    cancelRenewal_request: (state, { payload }: PayloadAction<{ id: string }>) => {},
    cancelRenewal_success: (state, { payload }: PayloadAction<{ id: string }>) => {
      delete state.byId[payload.id]?.renewal
    },
    amendSegments_request: (state, { payload }: PayloadAction<TAmendRequest<TSegmentFields>>) => {},
    amendSegments_success: (state, { payload }: PayloadAction<TSegmentFields & { id: string }>) => {
      const portfolio = state.byId[payload.id]
      if (portfolio) {
        portfolio.segments = payload.segments
      }
      state.summaries = state.summaries.map(summary =>
        summary.portfolioId === payload.id ? { ...summary, segments: payload.segments } : summary,
      )
    },
    updateTenantParties_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    updateTenantParties_success: {
      reducer: (state, { payload, meta }: PayloadAction<TPortfolio, string, { id: string }>) => {
        /** @todo update this? */
        state.byId[meta.id] = payload
      },
      prepare: (payload: TPortfolio, meta: { id: string }) => ({ payload, meta, error: null }),
    },
    updateOwnerParties_request: (state, { payload }: PayloadAction<TAmendRequest<unknown>>) => {},
    updateOwnerParties_success: {
      reducer: (state, { payload, meta }: PayloadAction<TPortfolio, string, { id: string }>) => {
        /** @todo update this? */
        state.byId[meta.id] = payload
      },
      prepare: (payload: TPortfolio, meta: { id: string }) => ({ payload, meta, error: null }),
    },
    addPartyAndLinkToLease_request: (
      state,
      { payload }: PayloadAction<{ id: string; values: TPartyPayload; type: 'tenant' | 'landlord' }>,
    ) => {},
    addPartyAndLinkToLease_success: () => {},
    bulkInvoicesCsvTemplate_request: () => {},
    bulkInvoicesCsvTemplate_success: (state, { payload }: PayloadAction<{ csvBase64: string }>) => {
      state.bulkInvoicesCsvTemplate = payload.csvBase64
    },
    getStatus_request: (state, { payload }: PayloadAction<string[]>) => {},
    getStatus_success: (state, { payload }: PayloadAction<TPortfolioStatusRow[]>) => {
      state.status = payload
    },

    /**
     * Applications
     */

    getApplications_request: (state, { payload }: PayloadAction<{ id: string }>) => {},
    getApplications_success: (state, { payload }: PayloadAction<TApplicationsResponse>) => {
      state.applications[payload.portfolioId] = payload.applications
    },
    addApplication_request: (
      state,
      { payload }: PayloadAction<{ params: { id: string }; body: TCreateApplicationRequest }>,
    ) => {},
    addApplication_success: (state, { payload }: PayloadAction<TApplicationsResponse>) => {
      state.applications[payload.portfolioId] = payload.applications
    },
    deleteApplication_request: (state, { payload }: PayloadAction<{ id: string; applicationId: string }>) => {},
    deleteApplication_success: (state, { payload }: PayloadAction<TApplicationsResponse>) => {
      state.applications[payload.portfolioId] = payload.applications
    },
    addApplicationAndLinkToLease_request: (
      state,
      { payload }: PayloadAction<{ id: string; values: TPartyPayload; applicationFee: number }>,
    ) => {},
    addApplicationAndLinkToLease_success: () => {},
    updateApplication_request: (
      state,
      { payload }: PayloadAction<{ id: string; values: TUpdateApplicationRequest; applicationId: string }>,
    ) => {},
    updateApplication_success: (state, { payload }: PayloadAction<TPortfolio>) => {},
    acceptApplication_request: (state, { payload }: PayloadAction<{ id: string; applicationId: string }>) => {},
    acceptApplication_success: (state, { payload }: PayloadAction<TPortfolio>) => {
      handlePortfolioResponse(state, payload)
    },
    sendApplicationInvoice_request: (
      state,
      { payload }: PayloadAction<{ portfolioId: string; applicationId: string }>,
    ) => {},
    /**
     * Written into `api/recon`'s `applicationInvoicesStatus`, not into this slice — see
     * the extraReducer in `api/recon/state`.
     */
    sendApplicationInvoice_success: (state, { payload }: PayloadAction<TSentApplicationInvoiceStatus>) => {},
  },
  extraReducers: builder => {
    builder.addCase(propertyApiEvents.propertyPortfolios_success, (state, { payload }) => {
      const portfolios = payload.response
      state.allIds = uniq([...state.allIds, ...portfolios.map(p => p.id)])
      portfolios.forEach(portfolio => {
        state.byId[portfolio.id] = portfolio
      })
    })
  },
})

const { reducer, actions: events } = portfolioApiSlice

export type PortfolioEvent = ActionType<typeof events>

export { events, initialState, reducer }
